/**
 * Pure helpers behind the games' fleet analytics events (@owlcat/app-shell
 * FleetEvents: maze_generated / maze_completed / maze_shared / signed_in).
 * Kept free of React so the mapping rules are unit-testable; the components
 * only call `track()` with what these return. Props are ids/enums/counts —
 * never a photo, a filename or a prompt.
 */
import type { MazeMode } from '@/contexts/games/components/maze/types/maze-types';

export type MazeKind = 'photo' | 'shape' | 'classic';
export type MazeShareChannel = 'print' | 'download_png' | 'download_svg';

/** Worker `mazeType` (image-maze / template-maze) → taxonomy kind; `isImage` wins when known. */
export function mazeKindFor(mazeType: string | undefined, isImage?: boolean): MazeKind {
  if (isImage === true) return 'photo';
  if (isImage === false) return 'shape';
  return mazeType === 'image-maze' ? 'photo' : 'shape';
}

/** Kind of the maze on screen for a given mode (template = shape, image = photo, rest = classic). */
export function mazeKindForMode(mode: MazeMode): MazeKind {
  if (mode === 'image') return 'photo';
  if (mode === 'template') return 'shape';
  return 'classic';
}

/**
 * Locally generated (rectangular/radial) mazes have no persisted id — an enum-like
 * stand-in keeps `maze_id` a required string without inventing per-render uuids.
 */
export function classicMazeId(mode: MazeMode): string {
  return `classic:${mode}`;
}

/** Timer ms → whole seconds for maze_completed. */
export function elapsedSeconds(ms: number): number {
  return Math.max(0, Math.round(ms / 1000));
}

const SIGNED_IN_KEY_PREFIX = 'owlcat:games:signed_in:';

/**
 * One-shot guard for `signed_in`: true the FIRST time this browser session sees
 * `identityId`, false on every later render / navigation (sessionStorage keyed
 * by the identity id, so a re-login as someone else still counts once).
 * A missing / throwing storage (private mode, SSR) degrades to "emit" — one
 * duplicate beats never recording a sign-in.
 */
export function claimSignedInOnce(storage: Storage | null | undefined, identityId: string): boolean {
  if (!identityId) return false;
  const key = SIGNED_IN_KEY_PREFIX + identityId;
  try {
    if (!storage) return true;
    if (storage.getItem(key) === '1') return false;
    storage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
}
